import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const MenuPreviewSection = () => {
  const navigate = useNavigate();

  const menuCategories = [
  {
    id: 1,
    name: "Express Delivery",
    description: "Fast, reliable delivery for urgent shipments worldwide",
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d",
    imageAlt: "Delivery person in uniform holding express package with fast shipping service",
    itemCount: 12,
    featured: true,
    color: "bg-primary",
    textColor: "text-primary-foreground"
  },
  {
    id: 2,
    name: "Freight Forwarding",
    description: "Comprehensive freight solutions for large-scale shipments",
    image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7",
    imageAlt: "Large cargo ship loaded with colorful shipping containers at international port",
    itemCount: 8,
    featured: false,
    color: "bg-accent",
    textColor: "text-accent-foreground"
  },
  {
    id: 3,
    name: "Air Cargo",
    description: "Premium air freight services for time-sensitive deliveries",
    image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158",
    imageAlt: "Commercial cargo airplane on runway ready for international air freight delivery",
    itemCount: 10,
    featured: false,
    color: "bg-warning",
    textColor: "text-warning-foreground"
  },
  {
    id: 4,
    name: "Ocean Shipping",
    description: "Cost-effective sea freight for bulk international cargo",
    image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec",
    imageAlt: "Container ship sailing through ocean carrying international freight cargo",
    itemCount: 6,
    featured: false,
    color: "bg-success",
    textColor: "text-success-foreground"
  },
  {
    id: 5,
    name: "Warehousing",
    description: "Secure storage solutions with inventory management",
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c",
    imageAlt: "Modern warehouse interior with organized shelving and inventory management system",
    itemCount: 7,
    featured: false,
    color: "bg-secondary",
    textColor: "text-secondary-foreground"
  },
  {
    id: 6,
    name: "Customs Clearance",
    description: "Expert customs brokerage and documentation services",
    image: "https://images.unsplash.com/photo-1565008576549-57569a49371d",
    imageAlt: "Customs officer reviewing international shipping documents and cargo clearance paperwork",
    itemCount: 15,
    featured: false,
    color: "bg-muted",
    textColor: "text-muted-foreground"
  }];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const handleCategoryClick = (category) => {
    navigate('/menu-catalog', { state: { selectedCategory: category?.name } });
  };

  const handleViewFullMenu = () => {
    navigate('/menu-catalog');
  };

  return (
    <section id="menu-preview" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16">

          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Package" size={20} className="text-primary" />
            <span className="text-primary font-body font-medium">OUR SERVICES</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Explore Our <span className="text-primary">Shipping</span> Solutions
          </h2>
          
          <p className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            From express delivery to freight forwarding, discover comprehensive logistics services tailored to your international shipping needs.
          </p>
        </motion.div>

        {/* Menu Categories Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">

          {menuCategories?.map((category) =>
          <motion.div
            key={category?.id}
            variants={cardVariants}
            whileHover={{
              y: -12,
              transition: { duration: 0.3 }
            }}
            onClick={() => handleCategoryClick(category)}
            className="group relative bg-card rounded-2xl overflow-hidden shadow-warm hover:shadow-warm-xl transition-all duration-300 cursor-pointer">

              {/* Featured Badge */}
              {category?.featured &&
            <div className="absolute top-4 left-4 z-20">
                  <div className="bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-body font-bold uppercase tracking-wide flex items-center space-x-1">
                    <Icon name="Star" size={12} />
                    <span>Featured</span>
                  </div>
                </div>
            }

              {/* Item Count Badge */}
              <div className="absolute top-4 right-4 z-20">
                <div className={`${category?.color} ${category?.textColor} px-3 py-1 rounded-full`}>
                  <span className="text-sm font-body font-bold">{category?.itemCount} items</span>
                </div>
              </div>

              {/* Image */}
              <div className="relative h-56 overflow-hidden">
                <Image
                src={category?.image}
                alt={category?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="bg-card/90 backdrop-blur-sm rounded-full p-3 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                    <Icon name="ArrowRight" size={24} className="text-primary" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-heading font-bold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                  {category?.name}
                </h3>
                
                <p className="text-muted-foreground font-body mb-4 line-clamp-2">
                  {category?.description}
                </p>

                {/* Action */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground font-body">
                    View all items
                  </span>
                  <Icon
                  name="ArrowRight"
                  size={18}
                  className="text-primary group-hover:translate-x-1 transition-transform duration-300" />

                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* View Full Menu Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="text-center">

          <Button
            variant="primary"
            size="lg"
            onClick={handleViewFullMenu}
            iconName="Menu"
            iconPosition="left"
            className="px-8 py-4 hover:scale-105 transition-transform duration-200 shadow-warm-lg">

            View Full Menu
          </Button>
        </motion.div>
      </div>
    </section>
  );


};

export default MenuPreviewSection;